import { useCallback, useEffect, useState } from 'react';
import { defaultBetBody, gameMap, serverConfigs } from '../consts';

export const useBetConfig = (authConfig, authTokens, updateLoading, updateResult, updateError, clearError) => {
  const [betBody, setBetBody] = useState(defaultBetBody);

  const updateBetBodyForGame = useCallback((gameId) => {
    const gameKey = Object.keys(gameMap).find((key) => gameMap[key].id === gameId);
    if (!gameKey) return;

    const gameData = gameMap[gameKey];

    setBetBody((prev) => {
      const updatedBetBody = JSON.parse(JSON.stringify(prev));

      const replaceInObject = (obj) => {
        Object.keys(obj).forEach((key) => {
          if (typeof obj[key] === 'string') {
            if (obj[key] === 'SLOT_ID' && gameData.SLOT_ID) {
              obj[key] = gameData.SLOT_ID;
            } else if (obj[key] === 'DEFAULT_LINE_SET_ID' && gameData.DEFAULT_LINE_SET_ID) {
              obj[key] = gameData.DEFAULT_LINE_SET_ID;
            } else if (obj[key] === 'SLOT_REEL_DEFAULT_ID' && gameData.SLOT_REEL_DEFAULT_ID) {
              obj[key] = gameData.SLOT_REEL_DEFAULT_ID;
            } else if (obj[key] === 'SLOT_REEL_FREE_SPINS_ID' && gameData.SLOT_REEL_FREE_SPINS_ID) {
              obj[key] = gameData.SLOT_REEL_FREE_SPINS_ID;
            } else if (
              obj[key] === 'SLOT_REEL_FREE_SPIN_BUY_FEATURE_ID' &&
              gameData.SLOT_REEL_FREE_SPIN_BUY_FEATURE_ID
            ) {
              obj[key] = gameData.SLOT_REEL_FREE_SPIN_BUY_FEATURE_ID;
            } else if (obj[key] === 'SLOT_REWARD_DEFAULT' && gameData.SLOT_REWARD_DEFAULT) {
              obj[key] = gameData.SLOT_REWARD_DEFAULT;
            }
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            replaceInObject(obj[key]);
          }
        });
      };

      replaceInObject(updatedBetBody);
      return updatedBetBody;
    });
  }, []);

  // Валидация конфигурации
  const validateConfig = useCallback(() => {
    const errors = [];

    if (!betBody.betPayload.coinAmount || betBody.betPayload.coinAmount <= 0) {
      errors.push('Coin Amount повинен бути більше 0');
    }

    if (!betBody.betPayload.coinValue || betBody.betPayload.coinValue <= 0) {
      errors.push('Coin Value повинен бути більше 0');
    }

    if (!betBody.config.slot.layout.cols || betBody.config.slot.layout.cols <= 0) {
      errors.push('Кількість колонок повинна бути більше 0');
    }

    if (!betBody.config.slot.layout.rows || betBody.config.slot.layout.rows <= 0) {
      errors.push('Кількість рядків повинна бути більше 0');
    }

    betBody.config.reelSets.forEach((reelSet, index) => {
      if (!reelSet.layout || reelSet.layout.length === 0) {
        errors.push(`Reel Set ${index + 1} не має layout`);
      }

      reelSet.layout.forEach((reel, reelIndex) => {
        if (!reel || reel.length === 0) {
          errors.push(`Reel ${reelIndex + 1} в Reel Set ${index + 1} порожній`);
        }
      });
    });

    return errors;
  }, [betBody]);

  // Симуляция ставок
  const simulateBet = useCallback(async () => {
    const sessionId = window.sessionStorage.getItem('sessionId') || authTokens.sessionId;

    if (!sessionId) {
      updateError('simulate', 'SessionId відсутній. Спочатку пройдіть авторизацію.');
      return false;
    }

    const validationErrors = validateConfig();
    if (validationErrors.length > 0) {
      updateError('simulate', 'Помилки валідації: ' + validationErrors.join(', '));
      return false;
    }

    updateLoading('simulate', true);
    clearError('simulate');

    try {
      const currentConfig = serverConfigs[authConfig.server];

      const finalBetBody = {
        ...betBody,
        betPayload: {
          ...betBody.betPayload,
          slotId: betBody.config.slot.id,
          clientId: currentConfig.clientId,
        },
        config: {
          ...betBody.config,
          clientSlotSettings: betBody.config.clientSlotSettings.map((setting) => ({
            ...setting,
            client_id: currentConfig.clientId,
          })),
        },
      };

      const graphqlUrl = currentConfig.simulateUrl;

      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authorization': sessionId,
          'user-agent': 'money.energy-internal-ua',
          'Origin': 'http://localhost:3500',
          'Access-Control-Request-Method': 'POST',
          'Access-Control-Request-Headers': 'authorization,content-type',
        },
        body: JSON.stringify(finalBetBody),
        signal: AbortSignal.timeout(120000),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      updateResult('simulate', data);
      return true;
    } catch (error) {
      updateError('simulate', error.message);
      updateResult('simulate', null);
      return false;
    } finally {
      updateLoading('simulate', false);
    }
  }, [betBody, authConfig, authTokens, validateConfig, updateLoading, clearError, updateResult, updateError]);

  // Сброс к дефолтным значениям
  const resetToDefaults = useCallback(() => {
    if (window.confirm('Скинути всі налаштування до значень за замовчуванням?')) {
      setBetBody(defaultBetBody);
    }
  }, []);

  // Экспорт конфигурации
  const exportConfig = useCallback(() => {
    const dataStr = JSON.stringify(betBody, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `betting-config-${new Date().toISOString().slice(0, 10)}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  }, [betBody]);

  // Импорт конфигурации
  const importConfig = useCallback((event, updateError) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          setBetBody(imported);
          updateError('import', null);
        } catch (error) {
          updateError('import', 'Помилка при імпорті JSON: ' + error.message);
        }
      };
      reader.readAsText(file);
    }
  }, []);

  // Инициализация при изменении игры
  useEffect(() => {
    if (defaultBetBody) {
      setBetBody(defaultBetBody);
      updateBetBodyForGame(authConfig.gameId);
    }
  }, [authConfig.gameId, updateBetBodyForGame]);

  return {
    // State
    betBody,
    setBetBody,

    // Actions
    updateBetBodyForGame,
    validateConfig,
    simulateBet,
    resetToDefaults,
    exportConfig,
    importConfig,
  };
};
